// Angular or Core
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
// Service
import { AuthHttpService } from '@core/services/auth-http.service';
import { CurrencyHttpService } from '@core/services/currency-http.service';
import { DropdownHttpService } from '@core/services/dropdown-http.service';
import { TransactionHttpService } from '@core/services/transaction-http.service';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { PwaUsageDataService } from './services/pwa-usage-data.service';
// Component
import { PwaMemberDetailsComponent } from './dialogs/pwa-member-details/pwa-member-details.component';
// Model
import { Pagination } from '@core/models/pagination.model';
import { PwaUsage } from '@core/models/pwa-usage.model';
// Third Party
import * as moment from 'moment-timezone';
import { Observable, of, Subscription } from 'rxjs';
import { finalize, map, tap } from 'rxjs/operators';

/** AIO-83768: dropdowns whose 'all' option means "no filter". A typed username of 'all' is a real search. */
const ALL_MEANS_NO_FILTER = ['currency_id', 'platform'];

/**
 * AIO-81798 — Members 1.7, the members who use the installed PWA.
 *
 * Two grains behind one table, switched by "Include anonymous":
 *   off — one row per member (a member with two devices is one row);
 *   on  — one row per device, including devices nobody has ever logged in on.
 *
 * Username and Currency are member attributes, so setting either necessarily hides anonymous
 * rows even with the toggle on.
 */
@Component({
  selector: 'kt-pwa-members',
  templateUrl: './pwa-members.component.html',
  styleUrls: ['./pwa-members.component.scss']
})
export class PwaMembersComponent implements OnInit, OnDestroy {

  form: FormGroup;

  pageSize: number;
  page = 1;
  maxSize = 5;
  pagination: Pagination;
  memberTotal = 0;

  dropdown = {
    perPage: this.dropdownHttpService.perPage,
    currencies: JSON.parse(sessionStorage.getItem('currencies')) === null ? [] : JSON.parse(sessionStorage.getItem('currencies')),
    /* Desktop is omitted on purpose: the desktop PWA is not a product we offer, so the option
       would be an empty result for every operator who tried it. It is not strictly unreachable —
       the tracker lives in projects/common and is compiled into the desktop app too, so a staff
       member using Chrome's "Create shortcut -> Open as window" can write a desktop row. Restore
       the option if desktop PWA ever ships. */
    platforms: [
      { id: 'ios', name: 'iOS' },
      { id: 'android', name: 'Android' }
      // { id: 'desktop', name: 'Desktop' }
    ]
  };

  ranges = this.transactionHttpService.ranges;
  dateTimePickerLocale = this.transactionHttpService.dateTimePickerLocale;
  timezone = JSON.parse(localStorage.getItem('user_data')).timezone;

  params = ``;

  pwaUsage$: Observable<PwaUsage[]>;

  sortingStorageName = 'sortingConfig';
  sortingStorageGroup = '1.7';
  /* The cleared state of the sort and the page size, beside defaultFilters and for the same
     reason: Clear resets all three, and a default written in two places is one that drifts. */
  private readonly defaultSortingConfig = {
    'device_count': 'desc',
    'first_launch_at': 'desc',
    'last_active_at': 'desc',
    'last_login_at': 'desc'
  };
  private readonly defaultSortingSelection = {
    'sort_by': 'last_active_at',
    'sort_order': 'desc',
  };
  private readonly defaultPageSize = 30;

  sortingConfig = { ...this.defaultSortingConfig };
  sortingSelection = { ...this.defaultSortingSelection };

  loading = false;
  clearBtnLoading = false;
  searchBtnLoading = false;
  dataLength: number;
  /** Counts onViewPageBy() calls, so a request can tell whether it is still the newest. */
  private requestSeq = 0;

  @ViewChild('dateRangeInput') private dateRangeInput: ElementRef<HTMLInputElement>;

  /** The cleared state of the filter form, shared by formInit() and onClear(). */
  private readonly defaultFilters = {
    username: null,
    currency_id: 'all',
    platform: 'all',
    show_anonymous: false,
    defaultDate: null,
    start_datetime: null,
    end_datetime: null,
  };

  private subscriptions = new Subscription();

  constructor(
    private dropdownHttpService: DropdownHttpService,
    private transactionHttpService: TransactionHttpService,
    private pwaUsageDataService: PwaUsageDataService,
    private currencyHttpService: CurrencyHttpService,
    private authHttpService: AuthHttpService,
    private loadingBar: LoadingBarService,
    public dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.pageSize = this.defaultPageSize;

    this.setCurrencyDropdown();
    this.storeSortingConfig();

    this.pagination = this.pwaUsageDataService.pagination;
    this.formInit();
    this.onSubmit();
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  /**
   * Is the table currently showing one row per member?
   *
   * Read from the submitted query, not the form: ticking Include without searching leaves the table
   * on the member grain, and the header has to match what is on screen. Device Count is only a real
   * number here — with anonymous rows shown every row is one device and the column is a constant 1,
   * so sorting it would do nothing and the header stops offering it.
   */
  get isMemberGrain(): boolean {
    return !(this.params || '').includes('show_anonymous=true');
  }

  /**
   * Mirror the sort into localStorage under sortingConfig, as the sibling list pages do. Nothing
   * reads it back - the page always opens on the default sort - so this keeps the key in step
   * with the other pages; it does not make the sort persist (AIO-83768).
   */
  private storeSortingConfig(): void {
    localStorage.setItem(
      this.sortingStorageName,
      JSON.stringify({ [this.sortingStorageGroup]: this.sortingConfig })
    );
  }

  onSortColumn(property: string) {
    if (property === 'device_count' && !this.isMemberGrain) {
      return;
    }

    // Reset other columns
    for (const key in this.sortingConfig) {
      if (Object.prototype.hasOwnProperty.call(this.sortingConfig, key)) {
        if (key === property) {
          this.sortingConfig[key] = this.sortingConfig[key] === 'asc' ? 'desc' : 'asc';
        } else {
          this.sortingConfig[key] = 'desc';
        }
      }
    }

    this.sortingSelection.sort_by = property;
    this.sortingSelection.sort_order = this.sortingConfig[property];

    this.onViewPageBy(this.page);
  }

  onSubmit(clearSearch?: boolean) {
    this.searchBtnLoading = clearSearch ? false : true;
    this.page = 1;

    of(this.form.value).pipe(
      map(this.filterFormFields),
      tap((data) => {
        /* The BO user picks a range in their own timezone; the API stores and compares UTC. */
        if (data['start_datetime']) {
          data['start_datetime'] = moment(data['start_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
          data['end_datetime'] = moment(data['end_datetime']).tz(this.timezone, true).utc().format('YYYY-MM-DD HH:mm:ss');
        }

        delete data['defaultDate'];

        this.params = Object.keys(data).map(key => key + '=' + data[key]).join('&');
      })
    ).subscribe(() => {
      /* AIO-83768: Device Count cannot be sorted on the device grain (see isMemberGrain).
         Switching to that grain with it selected kept sending sort_by=device_count, which the API
         ignores, and no header showed the order actually used. Fall back to the default sort. */
      if (!this.isMemberGrain && this.sortingSelection.sort_by === 'device_count') {
        this.sortingConfig = { ...this.defaultSortingConfig };
        this.sortingSelection = { ...this.defaultSortingSelection };
        this.storeSortingConfig();
      }

      this.onViewPageBy();
    });
  }

  onClear() {
    this.clearBtnLoading = true;
    this.page = 1;

    /* Clear means all of it, not only the filter boxes. The sort and the page size used to survive
       it, so an operator who cleared the page still had it ordered by whatever they had clicked
       last, with nothing on screen to say why. */
    this.sortingConfig = { ...this.defaultSortingConfig };
    this.sortingSelection = { ...this.defaultSortingSelection };
    this.pageSize = this.defaultPageSize;
    this.storeSortingConfig();

    /* reset() rather than rebuilding the FormGroup: a new group only reaches the date picker on
       the next change-detection pass, which would overwrite the blanked input right after we
       clear it. reset() writes through synchronously, so clearDateInput() below sticks. */
    this.form.reset(this.defaultFilters);
    this.clearDateInput();

    this.onSubmit(true);
  }

  onViewPageBy(page = 1) {
    this.loading = true;
    const params = this.params ? `&${this.params}` : '';
    this.loadingBar.start();
    const requestId = ++this.requestSeq;

    return this.pwaUsage$ = this.pwaUsageDataService.getWithQuery(`?page=${page}&perPage=${this.pageSize}&${this.generateSortingParam()}${params}`).pipe(
      tap((res: any) => {
        this.dataLength = res.length;
        this.pagination = this.pwaUsageDataService.pagination;
        this.memberTotal = this.pwaUsageDataService.memberTotal;
      }),
      /* AIO-83768: on success, on an error, and when the request is cancelled. In tap() alone, a
         failed request left Search and Clear disabled and the spinners running until a reload.

         Only the newest request resets the page. The async pipe cancels the old request when a new
         one replaces it - page 2 clicked, then page 3 - and that cancel runs this too: it stopped
         the spinner and re-enabled Search and Clear while page 3 was still loading, during change
         detection, which dev builds report as NG0100. */
      finalize(() => {
        if (requestId !== this.requestSeq) {
          return;
        }

        this.loading = false;
        this.clearBtnLoading = false;
        this.searchBtnLoading = false;
        this.loadingBar.complete();
      })
    );
  }

  /**
   * The PWA breakdown behind this row. Anonymous rows have no member_id, so the Actions cell
   * only offers it when one is present.
   */
  onOpenDetails(row: any) {
    if (!row.member_id) {
      return;
    }

    this.dialog.open(PwaMemberDetailsComponent, {
      width: '1200px',
      height: '80vh',
      autoFocus: false,
      data: { memberId: row.member_id, username: row.username }
    });
  }

  onPerPage(size: Event) {
    this.pageSize = +(size.target as HTMLSelectElement).value;
    this.onViewPageBy(this.page);
  }

  onDateRange(event: any) {
    /* AIO-83768: (change) also delivers the input's own DOM change event - after the operator
       types a range and leaves the field - and that has no startDate. Reading startDate._d from it
       threw. That event goes to onTypedDateRange(). */
    if (!event || !('startDate' in event)) {
      this.onTypedDateRange((event?.target as HTMLInputElement)?.value);
      return;
    }

    this.form.patchValue({
      start_datetime: event.startDate ? event.startDate._d : null,
      end_datetime: event.endDate ? event.endDate._d : null
    });
  }

  /**
   * AIO-83768: a range the operator typed. The picker moves its calendar to it but never emits
   * it, so the search ran without it while the box still showed it. Read the text in the picker's
   * own format; anything that is not a whole, valid range is cleared, so the box never shows dates
   * the search is not using.
   */
  private onTypedDateRange(text: string) {
    const format = this.dateTimePickerLocale.format;
    const parts = (text || '').split(' - ');
    const start = moment(parts[0]?.trim(), format, true);
    const end = moment(parts[1]?.trim(), format, true);

    if (parts.length !== 2 || !start.isValid() || !end.isValid() || end.isBefore(start)) {
      this.onClearDate();
      return;
    }

    this.form.patchValue({ start_datetime: start.toDate(), end_datetime: end.toDate() });
  }

  /**
   * Clearing through the form alone does not stick. patchValue(null) reaches the picker's
   * writeValue, which calls picker.clear() - and clear() resets the picker's internal range to
   * today rather than emptying it. The choosedDate it emits then re-enters writeValue with
   * {startDate: null, endDate: null}, an object, so the library recomputes a label from that
   * today range and writes it straight back into the input. Blanking the element afterwards is
   * what actually leaves the field empty.
   */
  onClearDate() {
    this.form.patchValue({ defaultDate: null, start_datetime: null, end_datetime: null });
    this.clearDateInput();
  }

  updateDateRange() {
    this.ranges = this.transactionHttpService.updateDateRange();
  }

  toLowerCaseInput(controlName: string, event: Event) {
    this.authHttpService.forceLowerCaseInputControl(this.form, controlName, event);
  }

  private setCurrencyDropdown() {
    if (this.dropdown.currencies.length === 0) {
      // set interval to get currencies from sessionStorage, to prevent returning empty currency dropdown
      if (sessionStorage.getItem('currencies') != undefined && sessionStorage.getItem('currencies') != null && sessionStorage.getItem('currencies') !== '') {
        var interval = setInterval(() => {
          this.dropdown.currencies = JSON.parse(sessionStorage.getItem('currencies'));
          if (this.dropdown.currencies != null) {
            clearInterval(interval);
          }
        }, 100);
      } else {
        const currencySub = this.currencyHttpService.setCurrency().subscribe(res => {
          this.dropdown.currencies = res;
        });
        this.subscriptions.add(currencySub);
      }
    }
  }

  /**
   * The date picker keeps rewriting the input after a clear: writeValue(null) makes the library
   * reset its internal range to today rather than emptying it, then the choosedDate it emits
   * re-enters writeValue with an object and a label is recomputed from that today range. Blanking
   * the element after the form has been written is what actually leaves the field empty.
   */
  private clearDateInput(): void {
    if (this.dateRangeInput) {
      this.dateRangeInput.nativeElement.value = '';
    }
  }

  private formInit() {
    this.form = new FormGroup({
      username: new FormControl(this.defaultFilters.username),
      currency_id: new FormControl(this.defaultFilters.currency_id),
      platform: new FormControl(this.defaultFilters.platform),
      show_anonymous: new FormControl(this.defaultFilters.show_anonymous),
      defaultDate: new FormControl(this.defaultFilters.defaultDate),
      start_datetime: new FormControl(this.defaultFilters.start_datetime),
      end_datetime: new FormControl(this.defaultFilters.end_datetime),
    });
  }

  private generateSortingParam() {
    return Object.keys(this.sortingSelection).map(key => key + '=' + this.sortingSelection[key]).join('&');
  }

  private filterFormFields(formData: any) {
    const fields = {};

    Object.keys(formData).forEach(key => {
      /* 'all' is the dropdowns' "no filter" value, and false for the toggle means the API
         default — sending either would only narrow the query for no reason. Only the dropdowns:
         a username typed as 'all' is a search, and dropping it listed every member (AIO-83768). */
      if (formData[key] !== '' && formData[key] !== null && formData[key] !== undefined
        && !(formData[key] === 'all' && ALL_MEANS_NO_FILTER.includes(key)) && formData[key] !== false) {
        fields[key] = formData[key];
      }
    });

    return fields;
  }

}
